import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { getCategoryConfig, STATUS_CONFIG } from "@/lib/taskCategoryConfig";

import { format } from "date-fns";
import { MoreVertical, Calendar as CalendarIcon, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AssigneeAvatarGroup } from "./AssigneeAvatarGroup";
import { AssignmentOverallStatus } from "./AssignmentOverallStatus";

interface EnhancedTaskCardProps {
  task: any;
  onToggleComplete: (taskId: string) => void;
  onViewDetails: (task: any) => void;
  onEdit: (task: any) => void;
  onDelete: (taskId: string) => void;
}

export const EnhancedTaskCard = ({ task, onToggleComplete, onViewDetails, onEdit, onDelete }: EnhancedTaskCardProps) => {
  const category = getCategoryConfig(task.category_type || 'other');
  const CategoryIcon = category.icon;
  
  // Determine effective status: if task has assignees, use their completion status
  let effectiveStatus = task.status;
  if (task.assignees && task.assignees.length > 0) {
    const allCompleted = task.assignees.every((a: any) => a.assignment_status === 'completed');
    const anyInProgress = task.assignees.some((a: any) => a.assignment_status === 'in_progress');
    const anyBlocked = task.assignees.some((a: any) => a.assignment_status === 'blocked');
    
    if (allCompleted) effectiveStatus = 'completed';
    else if (anyBlocked) effectiveStatus = 'blocked';
    else if (anyInProgress) effectiveStatus = 'in_progress';
  }
  
  const status = STATUS_CONFIG[effectiveStatus as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.not_started;
  
  const checkpoints = task.checkpoints || [];
  const completedCheckpoints = checkpoints.filter((cp: any) => cp.completed).length;
  const progress = checkpoints.length > 0 ? (completedCheckpoints / checkpoints.length) * 100 : 0;
  
  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && effectiveStatus !== 'completed';
  const daysUntilDue = task.due_date ? Math.ceil((new Date(task.due_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <Card 
      className={cn(
        "hover:shadow-md hover:bg-accent/50 transition-all cursor-pointer",
        effectiveStatus === 'completed' && "opacity-70"
      )}
      onClick={() => onViewDetails(task)}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <Checkbox
            checked={effectiveStatus === 'completed'}
            onCheckedChange={(checked) => {
              onToggleComplete(task.id);
            }}
            onClick={(e) => e.stopPropagation()}
            className="mt-1"
          />

          {/* Category Icon */}
          <div className={cn("p-2 rounded-lg", category.bgColor)}>
            <CategoryIcon className={cn("h-5 w-5", category.color)} />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Title Row */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className={cn(
                    "font-semibold text-lg",
                    effectiveStatus === 'completed' && "line-through"
                  )}>
                    {task.task_title}
                  </h3>
                  {task.assignees && task.assignees.length > 0 && (
                    <AssignmentOverallStatus assignees={task.assignees} variant="badge" size="small" />
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <Badge variant="outline" className="text-xs">
                    {category.name}
                  </Badge>
                  <Badge variant="outline" className={cn("text-xs", status.color)}>
                    {status.emoji} {status.label}
                  </Badge>
                  {task.is_admin_assigned && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                      <Star className="h-3 w-3 mr-1" />
                      Advisor Task
                    </Badge>
                  )}
                </div>
              </div>

              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails(task);
                  }}>
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => {
                    e.stopPropagation();
                    onEdit(task);
                  }}>
                    Edit Task
                  </DropdownMenuItem>
                  {!task.is_admin_assigned && (
                    <DropdownMenuItem onClick={(e) => {
                      e.stopPropagation();
                      onDelete(task.id);
                    }} className="text-destructive">
                      Delete Task
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Admin task context */}
            {task.is_admin_assigned && task.source_meeting_date && (
              <p className="text-xs text-amber-600 mb-2 italic">
                From your strategy session on {format(new Date(task.source_meeting_date), "MMM d, yyyy")}
              </p>
            )}

            {/* Description */}
            {task.task_description && (
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {task.task_description}
              </p>
            )}

            {/* Due Date */}
            {task.due_date && (
              <div className={cn(
                "flex items-center gap-2 text-sm mb-2",
                isOverdue ? "text-destructive" : "text-muted-foreground"
              )}>
                <CalendarIcon className="h-4 w-4" />
                <span>
                  Due: {format(new Date(task.due_date), "MMM d, yyyy")}
                  {daysUntilDue !== null && (
                    <span className="ml-1">
                      ({daysUntilDue > 0 ? `${daysUntilDue} days` : 'Overdue'})
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Checkpoints Progress */}
            {checkpoints.length > 0 && (
              <div className="mb-2">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">
                    Progress: {completedCheckpoints}/{checkpoints.length} checkpoints
                  </span>
                  <span className="text-muted-foreground">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
            )}

            {/* Assignees Display */}
            {task.assignees && task.assignees.length > 0 ? (
              <div className="mb-3">
                <AssigneeAvatarGroup 
                  assignees={task.assignees}
                  maxVisible={3}
                  size="medium"
                />
              </div>
            ) : (
              <div className="mb-3">
                <Badge variant="outline" className="text-muted-foreground border-dashed">
                  Unassigned
                </Badge>
              </div>
            )}

            {/* Tags */}
            {task.tags && task.tags.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {task.tags.slice(0, 3).map((tag: string, idx: number) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};